import { Modal, ModalBody, ModalFooter } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import type { CsvValidationFailure } from '@/App/tiles/shared/import/common';
import type { IntlMessageId } from '@/types/intl';

export interface ImportErrorModalProps<ERR extends string> {
  requiredColumns: string[];
  failure: CsvValidationFailure<ERR> | null;
  close: () => void;
}

const br = <br />;

interface EmphasizedIconProps {
  icon: string;
  color: 'info' | 'success' | 'warning' | 'danger';
}

export const EmphasizedIcon: React.FunctionComponent<EmphasizedIconProps> = ({ icon, color }) => (
  <div
    style={{ width: '3rem', height: '3rem' }}
    className={`d-flex align-items-center justify-content-center bg-${color} bg-opacity-10 rounded-circle`}
  >
    <i className={`icon icon-md text-${color} line-height-1`}>{icon}</i>
  </div>
);

export function ImportErrorModal<ERR extends string>({ requiredColumns, close, failure }: ImportErrorModalProps<ERR>) {
  const { formatList } = useIntl();

  const columnsList = formatList(requiredColumns);

  return (
    <Modal show={failure !== null} contentClassName="border border-danger">
      <div className="modal-header flex-column align-items-center text-center">
        <div className="mb-4">
          <EmphasizedIcon icon="warning" color="danger" />
        </div>
        <h4 className="modal-title text-danger mb-2">
          <FormattedMessage id="modal.import.errorModal.title" />
        </h4>
        <p className="text-secondary m-0">
          <FormattedMessage id="modal.import.errorModal.message" />
        </p>
      </div>
      <ModalBody className="pb-0">
        <div className="alert text-danger bg-danger bg-opacity-10" data-e2e="import-error-message">
          {failure !== null && (
            <FormattedMessage
              id={`modal.import.error.${failure.error}` as IntlMessageId}
              values={{
                count: failure.faultyLines.length,
                lines: failure.faultyLines.join(', '),
                columnsList,
                br,
              }}
            />
          )}
        </div>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-center">
        {/* oxlint-disable-next-line button-has-type -- not inside a form, default type is harmless here */}
        <button className="btn btn-lg btn-default" onClick={close} data-e2e="import-error-close-button">
          <FormattedMessage id="modal.import.errorModal.close" />
        </button>
      </ModalFooter>
    </Modal>
  );
}
